<template>
  <div class="groups-listing">
    <div class="d-flex">
      <div class="flex-fill">
        <SearchBox
          ref="searchBox"
          v-model:filtered="filteredGroups"
          placeholder="Search groups by name"
          class="mb-3"
          :data="groups"
          :features-to-query="['title']"
        />
      </div>
      <button
        v-if="!diff"
        class="btn btn-sm btn-primary ms-2"
        style="height: 37px"
        @click="openAllGroupsInNewTabs()"
      >
        Open All Groups in New Tabs
      </button>
    </div>
    <div
      v-if="groupsToShow().length > 0"
      class="scroller-container"
    >
      <!-- Single metric header for the whole diff report (grouping is not
           meaningful in diff mode). Sticks to the top of the scroller and reuses
           the exact fixed column widths of the per-group tables so it stays
           aligned with every group's rows below. -->
      <div
        v-if="diff"
        class="row diff-header"
        data-test="diff-header"
      >
        <div class="col-4" />
        <div class="col-8 pe-4">
          <table class="table table-borderless table-sm diff-fixed-layout">
            <thead>
              <tr>
                <th class="diff-name-col" />
                <th class="count-col">
                  {{ metricLabel }} (A)
                </th>
                <th class="count-col">
                  {{ metricLabel }} (B)
                </th>
                <th class="text-end delta-col">
                  &Delta; {{ metricLabel }}
                </th>
              </tr>
            </thead>
          </table>
        </div>
      </div>
      <div
        v-for="item in groupsToShow()"
        :key="item.entry.name"
        @click="$emit('groupLeftClicked', item.entry)"
        @click.middle="$emit('groupMiddleClicked', item.entry)"
      >
        <GroupSummary
          data-test="group-summary"
          class="border-bottom"
          :initial-title="item.queried.title"
          :group="item.entry"
          :total-rows="totalRows()"
          :diff="diff"
          :diff-metric="diffMetric"
          :total-rows-b="totalRowsB"
          @column-clicked="columnClicked"
          @title-changed="groupTitleChanged(item, $event)"
          @change-column-order="$emit('changeColumnOrder', item.entry)"
        />
      </div>
    </div>
    <em
      v-else
      class="ms-5 mt-2"
    >
      No group meets requested search query.
    </em>
  </div>
</template>

<script>
  import GroupSummary from './GroupSummary.vue'
  import SearchBox from './SearchBox.vue'
  export default {
    components: {SearchBox, GroupSummary},
    props: {
      data: {type: Object, default: null},
      diff: {type: Boolean, default: false},
      diffMetric: {type: String, default: "count"},
      totalRowsB: {type: Number, default: null},
    },
    emits: ['changeColumnOrder', 'columnClicked', 'groupLeftClicked', 'groupMiddleClicked', 'groupTitleChanged'],
    data() {
      // In diff mode Report.vue pre-marks the columns listed in either
      // report's `columnsToHide` (there is no File Overview tab to do it).
      const groups = this.data.groups.map(
        group => {
          const columns = group.columns.map(
            column => ({...column, isHidden: this.diff && Boolean(column.hiddenInDiff)})
          );
          return {
            ...group, columns,
            isHidden: this.diff && columns.every(column => column.isHidden),
          };
        }
      );
      return {isLoading: false, groups, filteredGroups: []};
    },
    computed: {
      metricLabel() {
        if (this.diffMetric === "uniques") return "Uniques";
        if (this.diffMetric === "coverage") return "Coverage";
        return "Count";
      },
    },
    methods: {
      totalRows() {return parseInt(this.data.totalRows.replaceAll(',', ''));},
      groupTitleChanged(group, title) {
        group.queried.title = title;
        group.entry.title = title;
        this.$emit('groupTitleChanged', group.entry);
      },
      groupsToShow() {
        return this.filteredGroups.filter(item => !item.entry.isHidden).map(
          group => (
            {
              index: group.index,
              entry: group.entry,
              queried: group.queried
            }
          )
        );
      },
      openAllGroupsInNewTabs() {
        this.groupsToShow().forEach(
          group => this.$emit('groupMiddleClicked', group.entry)
        );
      },
      columnClicked(clickType, column, group) {
        this.$emit('columnClicked', clickType, column, group);
      },
      isColumnInGroup(group, columnName) {
        return group.columns.map(column => column.name).includes(columnName);
      },
      groupByColumnName(columnName) {
        return this.groups.find(
          group => this.isColumnInGroup(group, columnName)
        );
      },
      columnEntry(group, columnName) {
        return group.columns.find(column => column.name === columnName);
      },
      setIsHidden(columnName, value) {
        const group = this.groupByColumnName(columnName);
        if (!group) {
          return;
        }
        const column = this.columnEntry(group, columnName);
        column.isHidden = value;
        if (group.data) {
          group.data[columnName].isHidden = value;
        }
        group.isHidden = group.columns.every(column => column.isHidden);
      },
      show(columnName) {
        this.setIsHidden(columnName, false);
      },
      hide(columnName) {
        this.setIsHidden(columnName, true);
      },
    }
  };
</script>

<style>
  .scroller-container {
    height: 100%;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    contain: strict;
  }
  .groups-listing {
    height: 100%;
    flex: 1;
    display: flex;
    flex-flow: column;
  }
  /* Single metric header that stays pinned to the top of the scroller while the
     groups scroll underneath it. */
  .diff-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
  }
  .diff-header th {
    font-weight: 900;
    padding-top: 0;
    padding-bottom: 0;
  }
  /* Full-width fixed layout with the exact percentage columns used by
     FileColumnStatistics.vue's diff table (and the same col-4 / col-8 split as
     GroupSummary.vue), so the single header lines up with every group's rows
     at any viewport width. */
  .diff-header .diff-fixed-layout {
    table-layout: fixed;
    width: 100%;
  }
  .diff-header .diff-name-col {
    width: 30%;
  }
  .diff-header .count-col {
    width: 21%;
  }
  .diff-header .delta-col {
    width: 28%;
  }
</style>
